import React from 'react';
import { SignIn, useAuth } from '@clerk/react';
import { useNavigate, Navigate } from '../../routes';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { requiresOperatorClerkOrg } from '@repo/shared-types';
import { useAppStore } from '../../store';
import { LoadingSpinner } from '../ui/LoadingSpinner';

/**
 * Production login via Clerk. Renders Clerk's SignIn with redirect to /app after sign-in.
 * When a Clerk session already exists, avoids mounting `<SignIn forceRedirectUrl="/app">`
 * during backend profile hydration — that combination previously caused an /app ↔ /login loop
 * (notably after `/demo` persona sign-in).
 */
export const ClerkLoginView: React.FC = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, signOut, orgId } = useAuth();
  const isAuthPending = useAppStore((s) => s.isAuthPending);
  const currentUserId = useAppStore((s) => s.currentUserId);
  const userRole = useAppStore((s) => s.userRole);
  const isSelfManagingOwner = useAppStore((s) => s.isSelfManagingOwner);
  const hasBackendProfile = Boolean(currentUserId?.trim());

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isSignedIn && isAuthPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isSignedIn && hasBackendProfile) {
    const needsOrg = requiresOperatorClerkOrg(userRole, isSelfManagingOwner === true);
    const target = needsOrg && !orgId ? ROUTES.SELECT_ORG : ROUTES.APP;
    return <Navigate to={target} replace />;
  }

  if (isSignedIn && !hasBackendProfile) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 text-center text-slate-200">
        <p className="max-w-md mb-6 text-slate-300 text-sm leading-relaxed">
          You are signed in with Clerk, but this environment could not load your workspace profile.
          Check that the API is running and reachable, then try again — or sign out and use the demo
          picker if you are on a seeded TestDB.
        </p>
        <button
          type="button"
          onClick={async () => {
            await signOut?.();
          }}
          className="rounded-full px-5 py-2 text-sm font-medium bg-white text-slate-900 hover:bg-slate-100 transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-20">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-600 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600 rounded-full blur-[100px]" />
      </div>
      <div className="absolute top-6 left-6 right-6 z-20 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => navigate('/?join=1')}
          className="text-sm text-slate-400 hover:text-white transition-colors"
        >
          Join the Ecosystem
        </button>
      </div>
      <div className="relative z-10">
        <SignIn
          signUpUrl={ROUTES.ROOT}
          fallbackRedirectUrl={ROUTES.APP}
          forceRedirectUrl={ROUTES.APP}
        />
      </div>
    </div>
  );
};

